import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "reactstrap";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import { projectsApis } from "../../../api/projects";
import "./WBSViewNew.css";
import { Drawer, Progress, Tooltip, Space, Divider, Button as AntButton } from "antd";
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
  ReloadOutlined,
  SettingOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { ReactComponent as PhaseIcon } from "../../../assets/icons/PhaseIcon.svg";
import { ReactComponent as ActivityIcon } from "../../../assets/icons/ActivityIcon.svg";
import { ReactComponent as TaskIcon } from "../../../assets/icons/TaskIcon.svg";
import {
  updatePhaseInDetailModalSlice,
  resetPhaseInDetailModalSlice,
} from "../../../store/slices/projectManagement/phase-view/PhaseInDetailView";
import {
  updateActivityInDetailModalSlice,
  resetActivityInDetailModalSlice,
} from "../../../store/slices/projectManagement/activity-view/ActivityInDetailView";
import PhaseQuickViewCard from "../phases/PhaseQuickViewCard";
import { useWbsName, useWbsTreeName } from "../shared/useWbsCodes";
import ActivityQuickViewCard from "../activity/ActivityQuickViewCard";

interface WBSNode {
  id: string;
  name: string;
  children?: WBSNode[];
  weightage?: number;
  taskCount?: number;
}

interface WBSData {
  projectName: string;
  completionPercentage?: number;
  phases: Array<{
    phaseId: number;
    phaseName: string;
    completionPercentage?: number;
    weightage?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    ownerName?: string;
    activities: Array<{
      activityId: number;
      completionPercentage?: number;
      name: string;
      weightage?: number;
      taskCount?: number;
      status?: string;
      startDate?: string;
      endDate?: string;
      ownerName?: string;
      tasks?: Array<{
        taskId: number;
        taskName: string;
        taskSequenceId: string;
        completionPercentage?: number;
        taskWeightagePercentage?: number;
      }>;
    }>;
  }>;
}

const WBSViewNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectName, id: projectId, derivedProjectStatus } = location.state || {};
  const [wbsData, setWbsData] = useState<WBSData | null>(null);
  // Phase/activity names embed a WBS code. Resolve labels against the real
  // hierarchy so a prefix is only kept where siblings would otherwise clash.
  const wbsLabelTree = useMemo(
    () =>
      (wbsData?.phases || []).map((phase: any) => ({
        key: `phase-${phase.phaseId}`,
        name: phase.phaseName,
        children: (phase.activities || []).map((activity: any) => ({
          key: `activity-${activity.activityId}`,
          name: activity.name,
        })),
      })),
    [wbsData]
  );
  const wbsLabel = useWbsTreeName(wbsLabelTree);
  const wbsName = useWbsName();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedActivityForTaskDetails, setSelectedActivityForTaskDetails] =
    useState<{
      activityId: number;
      activityName: string;
      completionPercentage?: number;
      phaseId?: number;
      phaseName?: string;
      phaseCompletionPercentage?: number;
      tasks: {
        taskId: number;
        taskName: string;
        taskSequenceId: string;
        completionPercentage?: number;
        taskWeightagePercentage?: number;
      }[];
    }>();

  // Phase and Activity drawer states
  const [phaseDrawer, setPhaseDrawer] = useState<{
    open: boolean;
    phase: any | null;
  }>({ open: false, phase: null });

  const [activityDrawer, setActivityDrawer] = useState<{
    open: boolean;
    activity: any | null;
  }>({ open: false, activity: null });


  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    fetchWbsData();
  }, [projectId]);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isFullscreen]);


  const fetchWbsData = async () => {
    if (!projectId) {
      setError("Project ID not found");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await projectsApis.fetchProjectWBS(projectId);
      const { data, status } = response || {};

      if (status) {
        setWbsData(data);
        if (data?.phases?.length <= 4) {
          setZoomLevel(100);
        } else if (data?.phases?.length <= 6) {
          setZoomLevel(78);
        }
      } else {
        setError("Failed to fetch WBS data");
      }
    } catch (err) {
      console.error("Error fetching WBS data:", err);
      setError("Error loading Work Breakdown Structure");
    } finally {
      setLoading(false);
    }
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 10, 50));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  // Open phase drawer
  const openPhaseDrawer = (phase: any) => {
    dispatch(
      updatePhaseInDetailModalSlice({
        open: true,
        id: phase?.phaseId,
        phaseName: phase?.phaseName,
        activeTab: "view",
        allowEdit: true,
        allowAdd: true,
        projectResponse: {
          id: projectId,
          name: projectName,
          status: derivedProjectStatus,
        },
        extra: {
          status: phase?.status,
          startDate: phase?.startDate,
          endDate: phase?.endDate,
          ownerName: phase?.ownerName,
        },
      })
    );
    setPhaseDrawer({ open: true, phase });
  };

  // Close phase drawer
  const closePhaseDrawer = () => {
    setPhaseDrawer({ open: false, phase: null });
    dispatch(resetPhaseInDetailModalSlice());
  };

  // Open activity drawer
  const openActivityDrawer = (activity: any, phase: any) => {
    dispatch(
      updateActivityInDetailModalSlice({
        open: true,
        id: activity?.activityId,
        activityName: activity?.name,
        activeTab: "view",
        allowEdit: true,
        allowAdd: true,
        projectResponse: {
          id: projectId,
          name: projectName,
          status: derivedProjectStatus,
        },
        phaseResponse: {
          id: phase?.phaseId,
          name: phase?.phaseName,
          status: phase?.status,
          startDate: phase?.startDate,
          endDate: phase?.endDate,
        },
        extra: {
          status: activity?.status,
          startDate: activity?.startDate,
          endDate: activity?.endDate,
          ownerName: activity?.ownerName,
        },
      })
    );
    setActivityDrawer({ open: true, activity: { ...activity, phase } });
  };

  // Close activity drawer
  const closeActivityDrawer = () => {
    setActivityDrawer({ open: false, activity: null });
    dispatch(resetActivityInDetailModalSlice());
  };

  const renderActivity = (
    activity: any,
    phase: any,
    phaseIndex: number,
    activityIndex: number
  ) => (
    <div key={activity.activityId} className="wbs-activity">
      <div
        className="wbs-node wbs-activity-node"
        onClick={() => openActivityDrawer(activity, phase)}
      >
        <div className="wbs-node-content">
          <div className="wbs-node-title">
            <ActivityIcon
              className="me-1"
              width={13}
              height={13}
              color="rgb(148,105,253)"
            />
            {wbsLabel(`activity-${activity.activityId}`, activity.name)}
          </div>
          {activity.weightage !== undefined && (
            <div className="wbs-node-meta">
              Weightage: {activity.weightage}%
            </div>
          )}
          <Tooltip title={`Completion %`} placement="top">
            <Progress
              strokeLinecap="butt"
              size={[150, 10]}
              percent={activity.completionPercentage || 0}
              status="active"
              strokeColor={{
                from: "rgba(173, 153, 223, 1)",
                to: "rgba(118, 100, 163, 1)",
              }}
            />
          </Tooltip>
        </div>
      </div>
      {activity.taskCount > 0 && (
        <div
          className="wbs-task-count"
          onClick={() => {
            setSelectedActivityForTaskDetails(() => ({
              activityId: activity.activityId,
              activityName: activity.name,
              completionPercentage: activity?.completionPercentage || 0,
              phaseId: phase?.phaseId,
              phaseName: phase?.phaseName,
              phaseCompletionPercentage: phase?.completionPercentage || 0,
              tasks: activity.tasks || [],
            }));
          }}
        >
          <div className="wbs-connector-line-horizontal"></div>
          <div className="wbs-node wbs-task-node">
            <div className="wbs-node-content">
              <div className="wbs-node-title">
                <TaskIcon className="me-1" width={13} height={13} />
                Task Count: {activity.taskCount}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPhase = (phase: any, phaseIndex: number) => (
    <div key={phase.phaseId} className="wbs-phase-container">
      <div className="wbs-connector-line-down"></div>
      <div className="wbs-phase">
        <div
          className="wbs-node wbs-phase-node"
          onClick={() => openPhaseDrawer(phase)}
        >
          <div className="wbs-node-content">
            <div className="wbs-node-title">
              <PhaseIcon
                className="me-1"
                width={13}
                height={13}
                color="rgb(0,202,120)"
              />
              {wbsLabel(`phase-${phase.phaseId}`, phase.phaseName)}
            </div>
            {phase.weightage !== undefined && (
              <div className="wbs-node-meta">Weightage: {phase.weightage}%</div>
            )}
            <Tooltip title={`Completion %`} placement="top">
              <Progress
                strokeLinecap="butt"
                size={[150, 10]}
                percent={phase.completionPercentage || 0}
                status="active"
                strokeColor={{
                  from: "rgba(183, 246, 219, 1)",
                  to: "rgba(7, 162, 98, 1)",
                }}
              />
            </Tooltip>
          </div>
        </div>
        {phase.activities && phase.activities.length > 0 && (
          <>
            <div className="wbs-connector-line-down"></div>
            <div className="wbs-activities-container">
              <div className="wbs-activities">
                {phase.activities.map((activity: any, actIndex: number) =>
                  renderActivity(activity, phase, phaseIndex, actIndex)
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Header with toolbar and legend
  const renderHeader = () => (
    <div className="wbs-header">
      <h5 className="wbs-title">
        {projectName || "Project"} / Work Breakdown Structure
      </h5>

      <div className="wbs-header-controls">
        {/* Legend */}
        <div className="wbs-legend-inline">
          <div className="wbs-legend-item">
            <div className="wbs-legend-color" style={{ background: "rgb(166,198,246)" }}></div>
            <span>Project</span>
          </div>
          <div className="wbs-legend-item">
            <div className="wbs-legend-color" style={{ background: "rgba(183, 246, 219, 1)" }}></div>
            <span>Phase</span>
          </div>
          <div className="wbs-legend-item">
            <div className="wbs-legend-color" style={{ background: "rgba(173, 153, 223, 1)" }}></div>
            <span>Activity</span>
          </div>
          <div className="wbs-legend-item">
            <div className="wbs-legend-color" style={{ background: "rgb(235, 235, 235)" }}></div>
            <span>Task</span>
          </div>
        </div>

        <Divider type="vertical" style={{ height: 24, margin: '0 12px' }} />

        {/* Zoom Controls */}
        <AntButton.Group size="small">
          <Tooltip title="Zoom Out">
            <AntButton
              icon={<ZoomOutOutlined />}
              onClick={handleZoomOut}
              disabled={zoomLevel <= 50}
            />
          </Tooltip>
          <AntButton className="wbs-zoom-display" disabled style={{ cursor: 'default', color: '#666' }}>
            {zoomLevel}%
          </AntButton>
          <Tooltip title="Zoom In">
            <AntButton
              icon={<ZoomInOutlined />}
              onClick={handleZoomIn}
              disabled={zoomLevel >= 200}
            />
          </Tooltip>
          <Tooltip title="Reset Zoom">
            <AntButton
              icon={<UndoOutlined />}
              onClick={handleResetZoom}
            />
          </Tooltip>
        </AntButton.Group>

        <Divider type="vertical" style={{ height: 24, margin: '0 8px' }} />

        {/* Actions */}
        <Space size="small">
          <Tooltip title="Refresh">
            <AntButton
              size="small"
              icon={<ReloadOutlined spin={loading} />}
              onClick={fetchWbsData}
              disabled={loading}
            />
          </Tooltip>
          <Tooltip title={isFullscreen ? "Exit Fullscreen (ESC)" : "Fullscreen"}>
            <AntButton
              size="small"
              type={isFullscreen ? "primary" : "default"}
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setIsFullscreen(!isFullscreen)}
            />
          </Tooltip>
          {/* Task Monitoring settings: which BOQ lines of this WBS the site is
              asked about each day. Same project state the sidebar passes, so
              the target page resolves the project without a re-select. */}
          <Tooltip title="Task Monitoring settings">
            <AntButton
              size="small"
              icon={<SettingOutlined />}
              disabled={!projectId}
              onClick={() =>
                navigate(`/pms/project/task-monitoring/${projectId}`, {
                  state: { ...(location.state || {}), id: projectId, projectId, projectName },
                })
              }
            />
          </Tooltip>
        </Space>
      </div>
    </div>
  );

  // Main WBS content
  const renderWBSContent = () => (
    <div
      className={`wbs-container ${isFullscreen ? "wbs-fullscreen" : ""}`}
      style={{
        // Documented exception to the single-scroll rule (docs/pms-design-system.md):
        // the WBS is a zoom/pan canvas and needs a bounded viewport.
        // 190px = 65px header + page header/toolbar chrome.
        height: isFullscreen ? "calc(100vh - 100px)" : "calc(100vh - 190px)",
        maxHeight: isFullscreen ? "calc(100vh - 100px)" : "calc(100vh - 190px)",
        minHeight: 500,
      }}
    >

      <div
        className="wbs-zoom-wrapper"
        style={{ transform: `scale(${zoomLevel / 100})` }}
      >
        <div className="wbs-root">
          <div className="wbs-node wbs-root-node">
            <div className="wbs-node-content">
              <div className="wbs-node-title">
                {wbsData?.projectName}
                <Tooltip title={`Completion %`} placement="top">
                  <Progress
                    strokeLinecap="butt"
                    size={[186, 10]}
                    percent={wbsData?.completionPercentage || 0}
                    status="active"
                    strokeColor={{
                      from: "rgb(166,198,246)",
                      to: "rgb(166,198,246)",
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        <div className="wbs-phases-container">
          {wbsData?.phases.map((phase, index) => renderPhase(phase, index))}
        </div>
      </div>
    </div>
  );

  // Render drawers
  const renderDrawers = () => (
    <>
      {/* Task Details Drawer */}
      <Drawer
        maskClosable={false}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>
                {wbsName(selectedActivityForTaskDetails?.activityName)}
              </strong>
              <div className="small text-muted mt-1">
                Activity Completion:{" "}
                {selectedActivityForTaskDetails?.completionPercentage || 0}%
              </div>
            </div>
          </div>
        }
        placement="right"
        width={600}
        onClose={() => setSelectedActivityForTaskDetails(undefined)}
        open={!!selectedActivityForTaskDetails?.activityId}
        styles={{ body: { paddingBottom: 80 } }}
        zIndex={isFullscreen ? 1100 : 1000}
      >
        <div className="p-2">
          {selectedActivityForTaskDetails?.tasks?.map((task) => (
            <div
              key={task.taskId}
              className="d-flex justify-content-between align-items-center mb-3 p-3 border rounded"
              style={{
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 8px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "";
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = "";
              }}
              onClick={() => {
                const phase = wbsData?.phases?.find((p) =>
                  p.activities?.some(
                    (a) =>
                      a.activityId ===
                      selectedActivityForTaskDetails?.activityId
                  )
                );
                const activity = phase?.activities?.find(
                  (a) =>
                    a.activityId === selectedActivityForTaskDetails?.activityId
                );

                // Navigate to task detail page with returnPath for back navigation
                navigate(`/pms/project/task-detail/${task.taskId}`, {
                  state: {
                    ...(location.state as any),
                    projectId: projectId,
                    projectName: projectName || "",
                    phaseId: phase?.phaseId,
                    phaseName: phase?.phaseName || "",
                    activityId: activity?.activityId,
                    activityName: activity?.name || "",
                    returnPath: `/pms/project/wbs/${projectId}`,
                    returnState: location.state,
                  },
                });
              }}
            >
              <div className="flex-grow-1">
                <div
                  className="mb-1"
                  style={{
                    fontWeight: 500,
                    fontSize: "13px",
                  }}
                >
                  {task.taskName}
                </div>
                <div
                  className="small"
                  style={{ color: "rgba(173, 153, 223, 0.8)" }}
                >
                  Task Weightage: {task.taskWeightagePercentage || 0}%
                </div>
              </div>
              <div className="ms-3">
                <Progress
                  type="circle"
                  size={60}
                  percent={task.completionPercentage}
                  strokeColor={{
                    "0%": "rgba(173, 153, 223, 1)",
                    "100%": "rgba(118, 100, 163, 1)",
                  }}
                  format={(percent) => `${percent}%`}
                />
              </div>
            </div>
          ))}

          {(!selectedActivityForTaskDetails?.tasks ||
            selectedActivityForTaskDetails.tasks.length === 0) && (
            <div className="text-center p-4">
              <div className="text-muted">
                No tasks available for this activity
              </div>
            </div>
          )}
        </div>
      </Drawer>

      {/* Phase Details Drawer */}
      <Drawer
        title={`${
          wbsLabel(
            `phase-${phaseDrawer.phase?.phaseId}`,
            phaseDrawer.phase?.phaseName
          ) || "Phase"
        } - Details`}
        placement="right"
        width={720}
        onClose={closePhaseDrawer}
        open={phaseDrawer.open}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
        zIndex={isFullscreen ? 1100 : 1000}
      >
        <PhaseQuickViewCard
          phaseId={phaseDrawer.phase?.phaseId}
          derivedProjectStatus={derivedProjectStatus}
          onClose={closePhaseDrawer}
          onRefresh={fetchWbsData}
        />
      </Drawer>

      {/* Activity Details Drawer */}
      <Drawer
        title={`${activityDrawer.activity?.name || "Activity"} - Details`}
        placement="right"
        width={720}
        onClose={closeActivityDrawer}
        open={activityDrawer.open}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
        zIndex={isFullscreen ? 1100 : 1000}
      >
        <ActivityQuickViewCard
          activityId={activityDrawer.activity?.activityId}
          derivedProjectStatus={derivedProjectStatus}
          onClose={closeActivityDrawer}
          onRefresh={fetchWbsData}
        />
      </Drawer>
    </>
  );

  if (loading) {
    return (
      <>
        <Head title="Work Breakdown Structure" />
        <Content>
          <Card className="card-bordered">
            <div className="card-inner" style={{ padding: "16px 24px" }}>
              <div className="wbs-header">
                <h5 className="wbs-title">
                  {projectName || "Project"} / Work Breakdown Structure
                </h5>
              </div>
              <div className="wbs-loading">
                <div className="wbs-spinner"></div>
                <p>Loading Work Breakdown Structure...</p>
              </div>
            </div>
          </Card>
        </Content>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Head title="Work Breakdown Structure" />
        <Content>
          <Card className="card-bordered">
            <div className="card-inner" style={{ padding: "16px 24px" }}>
              <div className="wbs-header">
                <h5 className="wbs-title">
                  {projectName || "Project"} / Work Breakdown Structure
                </h5>
              </div>
              <div className="wbs-error">
                <div className="wbs-error-icon">⚠️</div>
                <div className="wbs-error-title">Error Loading WBS</div>
                <div className="wbs-error-message">{error}</div>
                <button onClick={fetchWbsData} className="wbs-retry-button">
                  Retry
                </button>
              </div>
            </div>
          </Card>
        </Content>
      </>
    );
  }

  if (!wbsData || !wbsData.phases || wbsData.phases.length === 0) {
    return (
      <>
        <Head title="Work Breakdown Structure" />
        <Content>
          <Card className="card-bordered">
            <div className="card-inner" style={{ padding: "16px 24px" }}>
              <div className="wbs-header">
                <h5 className="wbs-title">
                  {projectName || "Project"} / Work Breakdown Structure
                </h5>
              </div>
              <div className="wbs-empty">
                No Work Breakdown Structure data available
              </div>
            </div>
          </Card>
        </Content>
      </>
    );
  }

  // Fullscreen mode - render via portal
  if (isFullscreen) {
    return (
      <>
        <Head title="Work Breakdown Structure" />
        <Content>
          <Card className="card-bordered">
            <div className="card-inner" style={{ padding: "16px 24px" }}>
              {renderHeader()}
            </div>
          </Card>
        </Content>
        {ReactDOM.createPortal(
          <>
            <div
              className="wbs-fullscreen-backdrop"
              onClick={() => setIsFullscreen(false)}
            />
            <div className="wbs-fullscreen-container">
              {renderHeader()}
              {renderWBSContent()}
            </div>
            {renderDrawers()}
          </>,
          document.body
        )}
      </>
    );
  }

  return (
    <>
      <Head title="Work Breakdown Structure" />
      <Content>
        <Card className="card-bordered">
          <div className="card-inner" style={{ padding: "16px 24px" }}>
            {renderHeader()}
            {renderWBSContent()}
          </div>
        </Card>
      </Content>
      {renderDrawers()}
    </>
  );
};

export default WBSViewNew;
